import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, Storage } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import { Plus } from 'lucide-react';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/toast';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import { LessonCard } from './components/LessonCard';
import { UploadLessonModal } from './components/UploadLessonModal';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    imageName?: string;
    name: string;
  };
  trainingVideos: Record<string, any[]> | null;
  appSettings: {
    entitlements: string[];
  };
};

interface Lesson {
  id: string;
  name: string;
  description: string;
  youTubeURL: string | null;
  url: string;
  urlType: string;
  order: number;
  imageName: string;
  imageType: string;
  posterImageName: string | null;
  category: string;
  createdAt: string;
  videoURL?: string;
}

export const getLessons = async (
  orgID: string,
  apiInstance: typeof API,
  callbackSetter: (lessons: Record<string, Lesson[]> | false) => void
) => {
  let myNextToken: string | null = null;
  const lessons: Lesson[] = [];

  const myQuery = gql`
    query MyQuery($orgID: ID!, $nextToken: String) {
      listLessonDetails(
        filter: { orgID: { eq: $orgID }, isDeleted: { eq: false } }
        limit: 200
        nextToken: $nextToken
      ) {
        nextToken
        items {
          id
          name
          description
          youTubeURL
          url
          urlType
          order
          imageName
          imageType
          posterImageName
          category
          createdAt
        }
      }
    }
  `;

  try {
    do {
      const res: any = await apiInstance.graphql({
        query: myQuery,
        variables: { orgID, nextToken: myNextToken },
      });
      const data = res.data.listLessonDetails.items;
      lessons.push(...data);
      myNextToken = res.data.listLessonDetails.nextToken;
    } while (myNextToken);
  } catch (error) {
    console.error('Error fetching lessons:', error);
    callbackSetter(false);
    return;
  }

  const lessonsObject: Record<string, Lesson[]> = {};
  for (const lesson of lessons) {
    if (lesson.posterImageName === '') {
      lesson.posterImageName = null;
    }
    if (lesson.youTubeURL === '') {
      lesson.youTubeURL = null;
    }
    if (lesson.youTubeURL) {
      if (lesson.youTubeURL.includes('watch?v=')) {
        lesson.youTubeURL = lesson.youTubeURL.split('watch?v=')[1];
      }
      if (!lesson.posterImageName) {
        lesson.posterImageName = `https://img.youtube.com/vi/${
          lesson.youTubeURL.split('&')[0]
        }/hqdefault.jpg`;
      } else if (
        !lesson.posterImageName.startsWith('https://img.youtube.com') &&
        !lesson.posterImageName.startsWith('https://i.vimeocdn.com')
      ) {
        const picture = await Storage.get(lesson.posterImageName);
        lesson.posterImageName = picture;
      }
      lesson.videoURL = `https://www.youtube.com/watch?v=${lesson.youTubeURL}`;
    } else {
      lesson.videoURL =
        'https://d22xeeaijfewl2.cloudfront.net/public/' +
        encodeURIComponent(lesson.imageName);
      if (
        lesson.posterImageName &&
        !lesson.posterImageName.startsWith('https://img.youtube.com') &&
        !lesson.posterImageName.startsWith('https://i.vimeocdn.com')
      ) {
        const picture = await Storage.get(lesson.posterImageName);
        lesson.posterImageName = picture;
      }
    }
    if (!lessonsObject[lesson.category]) {
      lessonsObject[lesson.category] = [];
    }
    lessonsObject[lesson.category].push(lesson);
  }

  Object.keys(lessonsObject).forEach((category) => {
    lessonsObject[category] = lessonsObject[category].sort(
      (a, b) => a.order - b.order
    );
  });

  callbackSetter(lessonsObject);
  global.trainingVideos = lessonsObject;
};

export default function TrainingHomePage() {
  const orgID = global.me.orgID;
  const navigate = useNavigate();
  const toast = useToast();
  const { checkPermission } = usePermissions();

  const canManage = checkPermission(PermissionKeys.Other_ManageTrainingVideos);

  const [allLessons, setAllLessons] = useState<Record<string, Lesson[]> | null>(
    null
  );
  const [recentUploads, setRecentUploads] = useState<Lesson[]>([]);
  const [uploadVid, setUploadVid] = useState(false);

  const recentUploadsReducer = useCallback(
    (lessonsObj: Record<string, Lesson[]> | null): Lesson[] => {
      if (!lessonsObj || typeof lessonsObj !== 'object') {
        return [];
      }
      return Object.values(lessonsObj)
        .reduce<Lesson[]>((acc, el) => [...acc, ...el], [])
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    },
    []
  );

  const refresh = useCallback(() => {
    getLessons(orgID, API, (res) => {
      if (res) {
        setAllLessons(res);
        setRecentUploads(recentUploadsReducer(res));
      } else {
        console.error('Failed to fetch lessons');
      }
    });
  }, [orgID, recentUploadsReducer]);

  useEffect(() => {
    if (global.trainingVideos) {
      setAllLessons(global.trainingVideos);
      setRecentUploads(recentUploadsReducer(global.trainingVideos));
    } else {
      getLessons(orgID, API, (res) => {
        if (res) {
          setAllLessons(res);
          setRecentUploads(recentUploadsReducer(res));
        } else {
          setAllLessons({});
          setRecentUploads([]);
        }
      });
    }
  }, []);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Training"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
        actions={
          canManage
            ? [
                {
                  id: 'add-media',
                  label: 'Add Media',
                  icon: <Plus className="size-4" />,
                  variant: 'primary',
                  onClick: () => {
                    setUploadVid(true);
                  },
                },
              ]
            : []
        }
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto space-y-8">
          {allLessons ? (
            <>
              {/* Recently Uploaded */}
              {recentUploads.length > 0 && (
                <CategorySection
                  title="Recently Uploaded"
                  lessons={recentUploads.slice(0, 4)}
                  refresh={refresh}
                  snackbar={(message: string, severity: string) => {
                    if (severity === 'error') {
                      toast.error(message);
                    } else {
                      toast.success(message);
                    }
                  }}
                />
              )}

              {/* Category Rows */}
              {Object.keys(allLessons).map((category) => (
                <CategorySection
                  key={category}
                  title={category}
                  lessons={allLessons[category]}
                  onViewAll={() => navigate(`/v2/training/${category}`)}
                  refresh={refresh}
                  snackbar={(message: string, severity: string) => {
                    if (severity === 'error') {
                      toast.error(message);
                    } else {
                      toast.success(message);
                    }
                  }}
                />
              ))}

              {Object.keys(allLessons).length === 0 &&
                recentUploads.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <p className="text-lg text-muted-foreground">
                      No training media yet.
                    </p>
                    {canManage && (
                      <Button
                        className="mt-4"
                        onClick={() => setUploadVid(true)}
                      >
                        <Plus className="size-4 mr-2" />
                        Add Your First Media
                      </Button>
                    )}
                  </div>
                )}
            </>
          ) : (
            <div className="space-y-8">
              <LoadingSkeleton />
              <LoadingSkeleton />
            </div>
          )}
        </div>
      </div>

      <UploadLessonModal
        open={uploadVid}
        onClose={() => setUploadVid(false)}
        refresh={refresh}
        snackbar={(message: string, severity: string) => {
          if (severity === 'error') {
            toast.error(message);
          } else {
            toast.success(message);
          }
        }}
      />
    </div>
  );
}

// ── Category Section ──────────────────────────────────────────

function CategorySection({
  title,
  lessons,
  onViewAll,
  refresh,
  snackbar,
}: {
  title: string;
  lessons: Lesson[];
  onViewAll?: () => void;
  refresh: () => void;
  snackbar: (message: string, severity: string) => void;
}) {
  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            View All
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {lessons.map((lesson) => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            refresh={refresh}
            display="grid"
            snackbar={snackbar}
          />
        ))}
      </div>
    </div>
  );
}

// ── Loading Skeleton ──────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div>
      <div className="h-6 w-40 bg-muted rounded animate-pulse mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-lg overflow-hidden border border-border bg-card shadow-sm"
          >
            <div className="aspect-video bg-muted animate-pulse" />
            <div className="p-3 space-y-2">
              <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
              <div className="h-3 w-full bg-muted/60 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
